import {
  Alert,
  AlertIcon,
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
  Spinner,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { NumericInput } from "../../../../../common/input/NumericInput";
import { TipTap } from "../../../../../common/input/TipTap/TipTap";
import { CpvSelector } from "../../../../../common/cpv/CpvSelector";
import { RadioOptionGroup } from "../../../../../common/input/RadioOptionGroup";
import { ContractPeriodSection } from "../components/ContractPeriodSection";
import { AdvancedSettingsSection } from "../components/AdvancedSettingsSection";
import { StepNavigation } from "../../../../../common/StepNavigation";
import {
  ContractNature,
  Currency,
  type SourcingEventV2Dto,
  type SourcingEventV2PatchDto,
} from "../../../../../autogen/bff-api";
import { CONTRACT_NATURE_OPTIONS } from "../utils/publicProcurementOptions";
import { getThresholdInfo } from "../utils/getThresholdInfo";
import { useEditor, useFieldErrorTranslation } from "../../../../../common/editing/save-coordinator";

interface Props {
  eventId: string;
  nextStep?: () => void;
  nextStepLabel?: string;
}

export const InfoStep = ({ eventId, nextStep, nextStepLabel }: Props) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus, fieldError } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  const formatFieldError = useFieldErrorTranslation();

  const title = state.eventData.title;
  const description = state.eventData.description ?? "";
  const estimatedValue = state.publicContext.estimatedValue?.value ?? null;
  const contractNature = state.publicContext.contractNature ?? "";
  const cpvCodes = state.publicContext.cpvCodes ?? [];
  const thresholdInfo = getThresholdInfo(estimatedValue ? Number(estimatedValue) : null, t);

  const isSaving = (field: string) => fieldStatus(field) === "saving" || fieldStatus(field) === "pending";
  const titleFieldError = fieldError("eventData.title");
  const estimatedValueFieldError = fieldError("publicContext.estimatedValue.value");
  const cpvFieldError = fieldError("publicContext.cpvCodes");
  const cpvHasAnyError = fieldStatus("publicContext.cpvCodes") === "error";

  return (
    <Box pb="8" pt="10">
      <Box borderWidth="1px" borderRadius="lg" p="5" mb="8">
        <FormLabel fontWeight="bold" fontSize="md" mb="4">
          {t("General information")}
        </FormLabel>

        <FormControl isRequired mb="6" isInvalid={!!titleFieldError}>
          <Flex align="center" gap="1">
            <FormLabel mb="0">{t("Title")}</FormLabel>
            {isSaving("eventData.title") && <Spinner size="xs" />}
          </Flex>
          <Input
            type="text"
            value={title}
            placeholder={t("E.g. Office supplies for municipality of Oslo") ?? undefined}
            onChange={(e) => updateField({ eventData: { title: e.target.value } }, { debounceMs: 300 })}
          />
          {titleFieldError ? (
            <FormErrorMessage>{formatFieldError(titleFieldError)}</FormErrorMessage>
          ) : (
            <FormHelperText>{t("A short, descriptive title for the procurement")}</FormHelperText>
          )}
        </FormControl>

        <FormControl mb="2">
          <FormLabel>
            {t("Description")} {isSaving("eventData.description") && <Spinner size="xs" />}
          </FormLabel>
          <TipTap
            content={description}
            onUpdate={async (value) => updateField({ eventData: { description: value } })}
            editable={true}
            debounceWait={300}
          />
          <FormHelperText>{t("Describe the scope and requirements of this procurement")}</FormHelperText>
        </FormControl>
      </Box>

      <Box borderWidth="1px" borderRadius="lg" p="5" mb="8" bg="smBackground">
        <FormLabel fontWeight="bold" fontSize="md" mb="4">
          {t("Classification and value")}
        </FormLabel>

        <FormControl mb="6" isInvalid={cpvHasAnyError}>
          <FormLabel>
            {t("CPV codes")} {isSaving("publicContext.cpvCodes") && <Spinner size="xs" />}
          </FormLabel>
          <CpvSelector
            value={cpvCodes}
            onChange={(next) =>
              updateField({ publicContext: { cpvCodes: next } }, { debounceMs: 300 })
            }
            isInvalid={cpvHasAnyError}
          />
          {cpvFieldError && <FormErrorMessage>{formatFieldError(cpvFieldError)}</FormErrorMessage>}
        </FormControl>

        <FormControl mb="6" isInvalid={!!estimatedValueFieldError}>
          <FormLabel>
            {t("Estimated value")} {isSaving("publicContext.estimatedValue") && <Spinner size="xs" />}
          </FormLabel>
          <InputGroup>
            <NumericInput
              numericValue={estimatedValue ? Number(estimatedValue) : null}
              placeholder="0"
              onNumericChange={(value) =>
                updateField(
                  { publicContext: { estimatedValue: { value, currency: Currency.Nok } } },
                  { debounceMs: 300 },
                )
              }
            />
            <InputRightAddon>{t("NOK")}</InputRightAddon>
          </InputGroup>
          {estimatedValueFieldError && (
            <FormErrorMessage>{formatFieldError(estimatedValueFieldError)}</FormErrorMessage>
          )}
          {thresholdInfo && !estimatedValueFieldError && (
            <Alert status={thresholdInfo.colorScheme as "info" | "warning"} mt="2" borderRadius="md" fontSize="sm">
              <AlertIcon />
              {thresholdInfo.message}
            </Alert>
          )}
        </FormControl>

        <FormControl>
          <FormLabel>
            {t("Contract type")} {isSaving("publicContext.contractNature") && <Spinner size="xs" />}
          </FormLabel>
          <RadioOptionGroup
            options={CONTRACT_NATURE_OPTIONS}
            value={contractNature}
            onChange={(value) => updateField({ publicContext: { contractNature: value as ContractNature } })}
          />
        </FormControl>
      </Box>

      <ContractPeriodSection eventId={eventId} />

      <AdvancedSettingsSection eventId={eventId} />

      <StepNavigation onNext={nextStep} nextLabel={nextStepLabel} />
    </Box>
  );
};
